import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { lazy, Suspense, useEffect, useState } from 'react'
import { syncOnBoot } from './game/cloudsave'
import { isMusicMuted, music, toggleMusicMute } from './game/music'
import { isMuted, toggleMute } from './game/sound'
import { loadRecord } from './game/storage'
import { baseSepolia } from 'wagmi/chains'
import WalletConnectButton from './components/WalletConnectButton'
import { isOnchainConfigured } from './web3/contract'
import { WEB_ECONOMY } from './config'
import Home from './pages/Home'

// Code-split: chaque page est un chunk chargé à la demande —
// la première visite ne paie que l'accueil.
const Mint = lazy(() => import('./pages/Mint'))
const Collection = lazy(() => import('./pages/Collection'))
const Battle = lazy(() => import('./pages/Battle'))
const TeamBattle = lazy(() => import('./pages/TeamBattle'))
const LiveWatch = lazy(() => import('./pages/LiveWatch'))
const PvpBattle = lazy(() => import('./pages/PvpBattle'))
const ProfileModal = lazy(() => import('./components/ProfileModal'))
const Legal = lazy(() => import('./pages/Legal'))
const League = lazy(() => import('./pages/League'))
const Market = lazy(() => import('./pages/Market'))
const MonsterDetail = lazy(() => import('./pages/MonsterDetail'))
const Pass = lazy(() => import('./pages/Pass'))
const Replay = lazy(() => import('./pages/Replay'))
const Recycler = lazy(() => import('./pages/Recycler'))
const EggPreview = lazy(() => import('./pages/EggPreview'))
const ArenaPreview = lazy(() => import('./pages/ArenaPreview'))

const NAV = [
  { to: '/', label: 'Jouer', icon: '🎮' },
  { to: '/league', label: 'Ligue', icon: '🏆' },
  { to: '/battle', label: 'Battle', icon: '⚔️' },
  { to: '/mint', label: 'Créer', icon: '✨' },
  // Marché: version web uniquement (retiré du build store — échanges crypto)
  ...(WEB_ECONOMY ? [{ to: '/market', label: 'Marché', icon: '🛒' }] : []),
  { to: '/collection', label: 'Collec.', icon: '🃏' },
]

export default function App() {
  const { isConnected, chainId } = useAccount()
  const onchainMode = isConnected && isOnchainConfigured && chainId === baseSepolia.id
  const [muted, setMuted] = useState(isMuted)
  const [musicMuted, setMusicMuted] = useState(isMusicMuted)
  const [profileOpen, setProfileOpen] = useState(false)
  // avatar relu à chaque fermeture de la modale (et à chaque navigation)
  const avatar = localStorage.getItem('cryptoket.avatar')
  const { pathname } = useLocation() // re-render à chaque navigation → la nav se déverrouille en jouant

  // sauvegarde cloud: restaure si le serveur a plus récent, sinon réplique
  useEffect(() => {
    void syncOnBoot()
  }, [])
  // la musique démarre au premier geste (règle d'autoplay des navigateurs)
  useEffect(() => {
    const kick = () => music.start()
    window.addEventListener('pointerdown', kick, { once: true })
    return () => window.removeEventListener('pointerdown', kick)
  }, [])
  // intensité: nerveuse en combat, posée partout ailleurs
  useEffect(() => {
    music.setMode(
      pathname === '/battle' || pathname === '/battle2v2' || pathname === '/battle-ranked' || pathname === '/replay' || pathname === '/live' || pathname === '/pvp' ? 'battle' : 'menu',
    )
  }, [pathname])

  // Exposition progressive: Ligue et Marché apparaissent après 2 victoires —
  // le nouveau joueur ne voit que l'essentiel (jouer, combattre, créer).
  const wins = loadRecord().wins
  const nav = NAV.filter((item) => wins >= 2 || (item.to !== '/league' && item.to !== '/market'))

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col px-3 pb-20 pt-4 sm:max-w-3xl">
      <header className="mb-4 flex items-center justify-between">
        <NavLink to="/" className="text-lg font-black tracking-tight">
          <span className="text-cyber">CRYPTO</span>KET
          <span className="ml-1 text-[10px] font-bold text-white/40">MONSTERS</span>
        </NavLink>
        <div className="flex items-center gap-2">
          <span
            className={`hidden rounded-full border px-2.5 py-1 text-[10px] font-bold sm:inline ${
              onchainMode ? 'border-cyber/40 bg-cyber/10 text-cyber' : 'border-line bg-panel text-white/50'
            }`}
          >
            {onchainMode ? '⛓ BASE SEPOLIA' : 'MODE DÉMO · LOCAL'}
          </span>
          <button
            onClick={() => setMusicMuted(toggleMusicMute())}
            title={musicMuted ? 'Activer la musique' : 'Couper la musique (garde les bruitages)'}
            className={`rounded-full border border-line bg-panel px-2 py-1 text-xs transition-colors hover:text-white ${
              musicMuted ? 'text-white/25 line-through' : 'text-white/60'
            }`}
          >
            🎵
          </button>
          <button
            onClick={() => setMuted(toggleMute())}
            title={muted ? 'Activer les sons' : 'Couper tous les sons'}
            className="rounded-full border border-line bg-panel px-2 py-1 text-xs text-white/60 transition-colors hover:text-white"
          >
            {muted ? '🔇' : '🔊'}
          </button>
          {/* Avatar/profil: visible seulement une fois connecté (gagne de la place) */}
          {isConnected && (
            <span className="relative shrink-0">
              <button
                onClick={() => setProfileOpen(true)}
                title="Profil & compte (déconnexion ici)"
                className="h-7 w-7 overflow-hidden rounded-full border border-cyber/60 bg-panel text-sm transition-transform active:scale-90"
              >
                {avatar?.startsWith('data:image/') ? (
                  <img src={avatar} alt="profil" className="h-full w-full object-cover" />
                ) : (
                  '👤'
                )}
              </button>
              <span className="pointer-events-none absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-stable ring-2 ring-carbon" />
            </span>
          )}
          <WalletConnectButton />
        </div>
      </header>

      {profileOpen && (
        <Suspense fallback={null}>
          <ProfileModal onClose={() => setProfileOpen(false)} />
        </Suspense>
      )}

      <main className="flex-1">
        <Suspense fallback={<div className="py-16 text-center text-sm text-white/30">…</div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/league" element={<League />} />
          {/* Marché: web uniquement — en build store, on renvoie à l'accueil */}
          <Route path="/market" element={WEB_ECONOMY ? <Market /> : <Navigate to="/" replace />} />
          <Route path="/mint" element={<Mint />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="/battle" element={<TeamBattle />} />
          <Route path="/battle-ranked" element={<Battle />} />
          <Route path="/live" element={<LiveWatch />} />
          <Route path="/pvp" element={<PvpBattle />} />
          <Route path="/legal" element={<Legal />} />
          {/* compat: l'ancien lien 2v2 mène au combat unifié */}
          <Route path="/battle2v2" element={<TeamBattle />} />
          <Route path="/monster/:id" element={<MonsterDetail />} />
          <Route path="/pass" element={<Pass />} />
          <Route path="/replay" element={<Replay />} />
          <Route path="/recycler" element={<Recycler />} />
          {import.meta.env.DEV && <Route path="/egg-preview" element={<EggPreview />} />}
          {import.meta.env.DEV && <Route path="/arena-preview" element={<ArenaPreview />} />}
        </Routes>
        </Suspense>
      </main>

      <nav className="fixed inset-x-0 bottom-0 border-t border-line bg-panel/90 backdrop-blur">
        <div className="mx-auto flex max-w-lg sm:max-w-3xl">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center py-2 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                  isActive ? 'text-cyber' : 'text-white/40 hover:text-white/70'
                }`
              }
            >
              <span className="text-base leading-tight">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
